import { useEffect, useId, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconButton } from './ui.tsx'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** Jak daleko se musi hlavicka stahnout dolu, aby se panel zavrel. */
const SWIPE_CLOSE_PX = 80

// Zasobnik otevrenych panelu: Escape a Tab obsluhuje jen ten nejvrchnejsi.
const openStack: symbol[] = []

function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getClientRects().length > 0,
  )
}

export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  closeLabel = 'Zavřít',
  className,
}: {
  open: boolean
  onClose(): void
  title: string
  subtitle?: string | undefined
  children: ReactNode
  footer?: ReactNode
  closeLabel?: string | undefined
  className?: string | undefined
}) {
  const titleId = useId()
  const subtitleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  // Rodic typicky predava novou arrow funkci pri kazdem renderu; efekt otevreni
  // na ni nesmi zaviset, jinak by kradl fokus pri kazde zmene.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  // Stazeni za hlavicku dolu zavre panel — uchyt nahore to na mobilu slibuje.
  // Na sirokem displeji je panel dialog uprostred a gesto tam nedava smysl.
  const swipe = useRef<{ y: number; pointerId: number } | null>(null)
  const [offset, setOffset] = useState(0)

  function onHeadPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (window.matchMedia('(min-width: 40rem)').matches) return
    if ((e.target as HTMLElement).closest('button')) return
    swipe.current = { y: e.clientY, pointerId: e.pointerId }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function onHeadPointerMove(e: PointerEvent<HTMLDivElement>) {
    const start = swipe.current
    if (!start || start.pointerId !== e.pointerId) return
    setOffset(Math.max(0, e.clientY - start.y))
  }

  function onHeadPointerEnd(e: PointerEvent<HTMLDivElement>) {
    const start = swipe.current
    if (!start || start.pointerId !== e.pointerId) return
    swipe.current = null
    const distance = Math.max(0, e.clientY - start.y)
    setOffset(0)
    if (e.type === 'pointerup' && distance > SWIPE_CLOSE_PX) onCloseRef.current()
  }

  useEffect(() => {
    if (!open) return
    const token = Symbol('sheet')
    openStack.push(token)
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current

    // autoFocus uvnitr panelu ma prednost.
    if (panel && !panel.contains(document.activeElement)) {
      const first = focusablesIn(panel)[0]
      ;(first ?? panel).focus()
    }
    document.documentElement.classList.add('sheet-open')

    function onKeyDown(event: KeyboardEvent) {
      if (openStack[openStack.length - 1] !== token || !panel) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusablesIn(panel)
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (!first || !last) {
        event.preventDefault()
        panel.focus()
      } else if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const index = openStack.indexOf(token)
      if (index !== -1) openStack.splice(index, 1)
      if (openStack.length === 0) document.documentElement.classList.remove('sheet-open')
      if (previous?.isConnected) previous.focus()
    }
  }, [open])

  if (!open) return null

  const hasFooter = footer !== undefined && footer !== null && footer !== false

  return createPortal(
    <div className="sheet-root">
      <div className="sheet-scrim" aria-hidden="true" onClick={() => onCloseRef.current()} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        className={['sheet-panel', hasFooter && 'has-footer', offset > 0 && 'is-swiping', className]
          .filter(Boolean)
          .join(' ')}
        style={offset > 0 ? { transform: `translateY(${offset}px)` } : undefined}
      >
        <div
          className="sheet-head"
          onPointerDown={onHeadPointerDown}
          onPointerMove={onHeadPointerMove}
          onPointerUp={onHeadPointerEnd}
          onPointerCancel={onHeadPointerEnd}
        >
          <div className="sheet-grab" aria-hidden="true" />
          <div className="sheet-head-row">
            <div className="sheet-titles">
              <h2 id={titleId} className="sheet-title">
                {title}
              </h2>
              {subtitle && (
                <p id={subtitleId} className="sheet-subtitle">
                  {subtitle}
                </p>
              )}
            </div>
            <IconButton icon="x" label={closeLabel} tone="muted" onClick={() => onCloseRef.current()} />
          </div>
        </div>
        <div className="sheet-body">{children}</div>
        {hasFooter && <div className="sheet-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
